if (!customElements.get('pw-size-guide')) {
  customElements.define('pw-size-guide', class extends HTMLElement {
    static photoForColor(photos, color, size) {
      const map = size ? photos?.[size] : photos;
      if (!map || typeof map !== 'object') return null;
      const usable = photo => photo && typeof photo === 'object' && (photo.angle || photo.front);
      return usable(map[color]) ? map[color] : Object.values(map).find(usable) || null;
    }
    static commonColor(maps, color) {
      const usable = maps.filter(map => map && Object.keys(map).length);
      const shared = usable.length ? Object.keys(usable[0]).filter(key => usable.every(map => map[key]?.angle || map[key]?.front)) : [];
      return shared.includes(color) ? color : shared[0] || color;
    }
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-size-unit]');
        if (!button) return;
        const unit = button.dataset.sizeUnit;
        const scope = this.closest('[id^="MainProduct-"]') || this;
        scope.querySelectorAll('.pw-size-number').forEach(value => {
          if (value.dataset[unit]) value.textContent = value.dataset[unit];
        });
        scope.querySelectorAll('[data-size-unit]').forEach(control => {
          control.setAttribute('aria-pressed', String(control.dataset.sizeUnit === unit));
        });
      });
      const parent = this.closest('details');
      const mapElement = this.querySelector('[data-pw-photo-map]');
      let photos = null;
      try { photos = mapElement ? JSON.parse(mapElement.textContent) : null; } catch (_) { /* A malformed sibling must not block the remaining guides. */ }
      let color = this.dataset.currentColor;
      const comparison = this.closest('.pw-handbag-compare');
      const sharedColor = () => {
        if (!comparison) return color;
        const maps = Array.from(comparison.querySelectorAll('[data-pw-photo-map]')).map(node => JSON.parse(node.textContent));
        if (!maps.length) return color;
        const shared = Object.keys(maps[0]).filter(key => maps.every(map => map[key]));
        return this.constructor.commonColor(maps, color);
      };
      const loadImages = () => {
        if (comparison) {
          color = sharedColor();
          const colorLabel = comparison.querySelector('[data-pw-handbag-color]');
          if (colorLabel && photos && photos[color]) colorLabel.textContent = photos[color].color;
          const widths = Array.from(comparison.querySelectorAll('.pw-angle-width .pw-size-number')).map(node => parseFloat(node.dataset.in)).filter(value => value > 0);
          const maxWidth = Math.max(...widths);
          const ownWidth = parseFloat(this.querySelector('.pw-angle-width .pw-size-number')?.dataset.in);
          if (ownWidth > 0 && maxWidth > 0) this.style.setProperty('--pw-size-scale', String(ownWidth / maxWidth));
        }
        let pairColor = color;
        if (photos && photos.small && (!photos.small[pairColor] || !photos.medium?.[pairColor])) {
          pairColor = this.constructor.commonColor([photos.small, photos.medium], color);
        }
        this.querySelectorAll('[data-pw-size-image]').forEach(image => {
          if (image.closest('pw-size-guide') !== this) return;
          const photo = this.constructor.photoForColor(photos, image.dataset.photoSize ? pairColor : color, image.dataset.photoSize);
          let source = photo ? photo[image.dataset.photoKind || 'front'] || photo.front || photo.angle : image.dataset.src;
          if (!source) return;
          const frame = image.closest('svg') || image;
          const displayWidth = frame.getBoundingClientRect().width || (this.closest('details')?.getBoundingClientRect().width || 600) / (photos?.small ? 2 : 1);
          if (displayWidth > 0) {
            const url = new URL(source, window.location.origin);
            if (url.pathname.includes('/cdn/shop/') || url.hostname === 'cdn.shopify.com') {
              const width = Math.min(1000, Math.max(300, Math.ceil(displayWidth * (window.devicePixelRatio || 1) / 100) * 100));
              url.searchParams.set('width', String(width));
              source = url.href;
            }
          }
          const attribute = image.namespaceURI === 'http://www.w3.org/2000/svg' ? 'href' : 'src';
          if (image.getAttribute(attribute) !== source) {
            image.addEventListener('error', () => {
              if (image.getAttribute(attribute) === source && photo?.front && photo.front !== photo.angle) image.setAttribute(attribute, photo.front);
            }, { once: true });
            image.setAttribute(attribute, source);
          }
          if (image.dataset.photoSize && photos?.small) {
            const small = this.constructor.photoForColor(photos, pairColor, 'small');
            const medium = this.constructor.photoForColor(photos, pairColor, 'medium');
            const different = small?.color !== medium?.color;
            const card = image.closest('.pw-size-photo-frame')?.parentElement;
            let caption = card?.querySelector('[data-pw-photo-caption]');
            if (card && !caption) {
              caption = document.createElement('small');
              caption.dataset.pwPhotoCaption = '';
              caption.className = 'pw-photo-caption';
              card.append(caption);
            }
            if (caption) caption.textContent = [different ? photo?.color : '', photo?.visualization ? 'Color visualization' : ''].filter(Boolean).join(' · ');
          }
          const diagram = image.closest('[data-pw-measure-diagram]');
          if (diagram && photo?.bounds) {
            const [left, top, right, bottom, bodyTop] = photo.bounds;
            const height = 1000 / (photo.aspect || 1);
            const comparisonWidths = Array.from(this.querySelectorAll('[data-pw-width-label] .pw-size-number')).map(node => parseFloat(node.dataset.in)).filter(value => value > 0);
            const ownWidth = parseFloat(diagram.querySelector('[data-pw-width-label] .pw-size-number')?.dataset.in);
            const relative = image.dataset.photoSize && ownWidth > 0 ? ownWidth / Math.max(...comparisonWidths) : parseFloat(this.style.getPropertyValue('--pw-size-scale')) || 1;
            const scale = Math.min(600 / ((right - left) * 1000), 760 / ((bottom - top) * height)) * relative;
            const bagWidth = (right - left) * 1000 * scale;
            const bagHeight = (bottom - top) * height * scale;
            const x = (1000 - bagWidth) / 2;
            const y = 850 - bagHeight;
            const bodyY = y + (bodyTop - top) * height * scale;
            image.setAttribute('x', x - left * 1000 * scale);
            image.setAttribute('y', y - top * height * scale);
            image.setAttribute('width', 1000 * scale);
            image.setAttribute('height', height * scale);
            const depth = Math.min(100, bagWidth * .18);
            const edge = x + bagWidth;
            const lineX = edge + 35;
            const centerY = (bodyY + 850) / 2;
            diagram.querySelector('[data-pw-width-line]').setAttribute('d', `M${x + depth} 875v25L${edge} 870v-25`);
            diagram.querySelector('[data-pw-depth-line]').setAttribute('d', `M${x - 10} 820l-20 20l${depth} 60l20 -20`);
            diagram.querySelector('[data-pw-height-line]').setAttribute('d', `M${lineX} ${bodyY}h25V850h-25`);
            const widthLabel = diagram.querySelector('[data-pw-width-label]');
            widthLabel.setAttribute('x', (x + depth + edge) / 2);
            widthLabel.setAttribute('y', 995);
            const heightLabel = diagram.querySelector('[data-pw-height-label]');
            heightLabel.setAttribute('x', lineX + 85);
            heightLabel.setAttribute('y', centerY);
            heightLabel.setAttribute('transform', `rotate(-90 ${lineX + 85} ${centerY})`);
            const depthLabel = diagram.querySelector('[data-pw-depth-label]');
            depthLabel.setAttribute('x', Math.max(115, x + depth / 2 - 30));
            depthLabel.setAttribute('y', 950);
          }
        });
        const label = this.querySelector('[data-pw-compare-color]');
        if (label && pairColor) {
          const small = this.constructor.photoForColor(photos, pairColor, 'small');
          const medium = this.constructor.photoForColor(photos, pairColor, 'medium');
          label.textContent = small?.color === medium?.color ? small?.color || '' : '';
        }
      };
      const scope = this.closest('[id^="MainProduct-"]');
      if (scope) scope.addEventListener('change', event => {
        if (event.target.name !== 'Color') return;
        color = event.target.value.toLowerCase();
        loadImages();
      });
      if (parent) parent.addEventListener('toggle', loadImages);
      // Warm only the selected-color guide as it approaches the viewport, after the hero.
      if ('IntersectionObserver' in window && parent && !parent.open) {
        this.observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) { loadImages(); this.observer.disconnect(); }
        }, { rootMargin: '300px' });
        this.observer.observe(parent);
      } else loadImages();
    }
    disconnectedCallback() { this.observer?.disconnect(); }
  });
}

